/**
 * Danmu replay: fans' takes and reactions resurface at the exact game moment they answered,
 * for anyone watching later. Pure + unit-tested.
 */
import type { ProbabilityPoint, Take } from '@clutch/core';
import { swingsBySeq } from '../reel/segments';

export type DanmuItem = {
  id: string;
  kind: 'take' | 'reaction';
  seq: number; // the game event it surfaces at
  fanId: string;
  name: string;
  emoji?: string;
  takeId?: string;
};

/** The one moment a take is "about": its biggest-swing mark, else its first mark, else where it started. */
export function takeAnchorSeq(take: Pick<Take, 'marks' | 'startedAtSeq'>, probability: readonly Pick<ProbabilityPoint, 'seq' | 'pHome'>[] = []): number {
  const swings = swingsBySeq(probability);
  let best: { seq: number; w: number } | undefined;
  for (const m of take.marks) {
    const w = swings.get(m.seq) ?? 0;
    if (w > 0 && (!best || w > best.w)) best = { seq: m.seq, w };
  }
  if (best) return best.seq;
  return take.marks[0]?.seq ?? Math.max(0, take.startedAtSeq);
}

/** Items that become due as the game moves from `prevSeq` to `curSeq` (exclusive → inclusive). A backward seek yields none. */
export function dueDanmu<T extends Pick<DanmuItem, 'seq'>>(items: readonly T[], prevSeq: number, curSeq: number): T[] {
  if (curSeq <= prevSeq) return [];
  return items.filter((i) => i.seq > prevSeq && i.seq <= curSeq).sort((a, b) => a.seq - b.seq);
}

/** Takes that cover a play (it arrived while that fan's camera rolled), newest first. */
export function takesCovering<T extends Pick<Take, 'marks' | 'createdAt'>>(takes: readonly T[], seq: number): T[] {
  return takes.filter((t) => t.marks.some((m) => m.seq === seq)).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** Lanes for flying items: spread consecutive items over `lanes` rows so they don't overlap. */
export const laneFor = (index: number, lanes = 3) => ((index % lanes) + lanes) % lanes;
